import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Link } from 'react-router-dom';

// Animated Product Development Illustration
const ProductDevelopmentIllustration = () => (
    <svg viewBox="0 0 400 400" className="w-full h-full">
        <defs>
            <linearGradient id="prodGradient" x1="0%" y1="0%" x2="100%" y2="100%">
                <stop offset="0%" stopColor="#0d9488" />
                <stop offset="100%" stopColor="#065f46" />
            </linearGradient>
            <filter id="glow">
                <feGaussianBlur stdDeviation="3.5" result="coloredBlur" />
                <feMerge>
                    <feMergeNode in="coloredBlur" />
                    <feMergeNode in="SourceGraphic" />
                </feMerge>
            </filter>
        </defs>

        {/* Code editor window */}
        <motion.rect
            x="60" y="60" width="280" height="200" rx="10"
            fill="#1e293b"
            stroke="#22d3ee"
            strokeWidth="2"
            animate={{ boxShadow: ['0 0 20px #22d3ee', '0 0 40px #22d3ee', '0 0 20px #22d3ee'] }}
            transition={{ duration: 2, repeat: Infinity }}
        />

        {/* Window controls */}
        <circle cx="85" cy="85" r="6" fill="#ef4444" />
        <circle cx="105" cy="85" r="6" fill="#fbbf24" />
        <circle cx="125" cy="85" r="6" fill="#22c55e" />

        {/* Code lines */}
        {[0, 1, 2, 3, 4, 5].map((line, i) => (
            <motion.rect
                key={`code-${i}`}
                x="80"
                y={110 + i * 22}
                width={100 + Math.random() * 120}
                height="12"
                rx="3"
                fill={i % 2 === 0 ? '#22d3ee' : '#8b5cf6'}
                opacity="0.6"
                initial={{ width: 0 }}
                animate={{ width: 100 + Math.random() * 120 }}
                transition={{ duration: 0.5, delay: i * 0.2, repeat: Infinity, repeatDelay: 3 }}
            />
        ))}

        {/* Floating devices */}
        <motion.g
            animate={{ y: [0, -10, 0] }}
            transition={{ duration: 3, repeat: Infinity }}
        >
            {/* Mobile */}
            <rect x="80" y="290" width="60" height="100" rx="8" fill="#0f172a" stroke="#22d3ee" strokeWidth="2" />
            <rect x="90" y="300" width="40" height="70" rx="2" fill="#22d3ee" opacity="0.3" />
        </motion.g>

        <motion.g
            animate={{ y: [0, -15, 0] }}
            transition={{ duration: 3.5, delay: 0.5, repeat: Infinity }}
        >
            {/* Tablet */}
            <rect x="170" y="290" width="80" height="100" rx="8" fill="#0f172a" stroke="#22d3ee" strokeWidth="2" />
            <rect x="180" y="300" width="60" height="75" rx="2" fill="#22d3ee" opacity="0.3" />
        </motion.g>

        <motion.g
            animate={{ y: [0, -12, 0] }}
            transition={{ duration: 4, delay: 1, repeat: Infinity }}
        >
            {/* Desktop */}
            <rect x="280" y="300" width="100" height="70" rx="5" fill="#0f172a" stroke="#22d3ee" strokeWidth="2" />
            <rect x="290" y="310" width="80" height="45" rx="2" fill="#22d3ee" opacity="0.3" />
            <rect x="310" y="370" width="40" height="15" rx="2" fill="#0f172a" stroke="#22d3ee" strokeWidth="1" />
        </motion.g>

        {/* Connection lines */}
        {[1, 2, 3].map((_, i) => (
            <motion.line
                key={`line-${i}`}
                x1={200}
                y1={260}
                x2={110 + i * 90}
                y2={290}
                stroke="#22d3ee"
                strokeWidth="2"
                strokeDasharray="5 5"
                animate={{ opacity: [0.3, 1, 0.3] }}
                transition={{ duration: 1.5, delay: i * 0.3, repeat: Infinity }}
            />
        ))}
    </svg>
);

// Animated MVP Development Illustration
const MVPDevelopmentIllustration = () => (
    <svg viewBox="0 0 400 400" className="w-full h-full">
        <defs>
            <linearGradient id="mvpGradient" x1="0%" y1="0%" x2="100%" y2="100%">
                <stop offset="0%" stopColor="#8b5cf6" />
                <stop offset="100%" stopColor="#6366f1" />
            </linearGradient>
        </defs>

        {/* Rocket */}
        <motion.g
            animate={{ y: [0, -20, 0], rotate: [0, 5, -5, 0] }}
            transition={{ duration: 4, repeat: Infinity }}
            style={{ originX: '200px', originY: '200px' }}
        >
            {/* Rocket body */}
            <motion.path
                d="M200 80 L230 180 L230 280 L200 300 L170 280 L170 180 Z"
                fill="url(#mvpGradient)"
                stroke="#22d3ee"
                strokeWidth="2"
            />
            {/* Rocket tip */}
            <motion.path
                d="M200 40 L230 100 L170 100 Z"
                fill="#22d3ee"
            />
            {/* Window */}
            <motion.circle
                cx="200" cy="160" r="25"
                fill="#0f172a"
                stroke="#22d3ee"
                strokeWidth="2"
                animate={{ fill: ['#0f172a', '#1e40af', '#0f172a'] }}
                transition={{ duration: 2, repeat: Infinity }}
            />
            {/* Fins */}
            <path d="M170 250 L140 300 L170 280 Z" fill="#8b5cf6" />
            <path d="M230 250 L260 300 L230 280 Z" fill="#8b5cf6" />
        </motion.g>

        {/* Flames */}
        <motion.g>
            {[0, 1, 2].map((_, i) => (
                <motion.ellipse
                    key={`flame-${i}`}
                    cx={185 + i * 15}
                    cy={320}
                    rx={8}
                    ry={30 + i * 5}
                    fill={i === 1 ? '#fbbf24' : '#f97316'}
                    animate={{
                        ry: [30 + i * 5, 45 + i * 5, 30 + i * 5],
                        opacity: [0.8, 1, 0.8]
                    }}
                    transition={{ duration: 0.3, delay: i * 0.1, repeat: Infinity }}
                />
            ))}
        </motion.g>

        {/* Stars */}
        {[...Array(15)].map((_, i) => (
            <motion.circle
                key={`star-${i}`}
                cx={50 + Math.random() * 300}
                cy={50 + Math.random() * 150}
                r="2"
                fill="white"
                animate={{ opacity: [0.2, 1, 0.2], scale: [1, 1.5, 1] }}
                transition={{ duration: 1.5, delay: i * 0.2, repeat: Infinity }}
            />
        ))}

        {/* Speed lines */}
        {[...Array(8)].map((_, i) => (
            <motion.line
                key={`speed-${i}`}
                x1={80 + i * 30}
                y1={350}
                x2={80 + i * 30}
                y2={380}
                stroke="#22d3ee"
                strokeWidth="3"
                strokeLinecap="round"
                opacity="0.5"
                animate={{ y: [0, -20, 0], opacity: [0.2, 0.8, 0.2] }}
                transition={{ duration: 0.5, delay: i * 0.1, repeat: Infinity }}
            />
        ))}
    </svg>
);

// Animated AI/ML Illustration
const AIMLIllustration = () => (
    <svg viewBox="0 0 400 400" className="w-full h-full">
        <defs>
            <linearGradient id="aiGradient" x1="0%" y1="0%" x2="100%" y2="100%">
                <stop offset="0%" stopColor="#06b6d4" />
                <stop offset="100%" stopColor="#8b5cf6" />
            </linearGradient>
        </defs>

        {/* Brain outline */}
        <motion.path
            d="M200 80 Q260 80 280 140 Q320 160 300 220 Q310 280 260 300 Q240 340 200 340 Q160 340 140 300 Q90 280 100 220 Q80 160 120 140 Q140 80 200 80"
            fill="none"
            stroke="url(#aiGradient)"
            strokeWidth="3"
            animate={{ pathLength: [0, 1] }}
            transition={{ duration: 2, repeat: Infinity, repeatDelay: 1 }}
        />

        {/* Neural network nodes */}
        {[
            { x: 200, y: 120 },
            { x: 140, y: 160 },
            { x: 260, y: 160 },
            { x: 120, y: 220 },
            { x: 200, y: 200 },
            { x: 280, y: 220 },
            { x: 150, y: 280 },
            { x: 250, y: 280 },
            { x: 200, y: 320 },
        ].map((node, i) => (
            <motion.circle
                key={`node-${i}`}
                cx={node.x}
                cy={node.y}
                r="12"
                fill="#22d3ee"
                animate={{
                    scale: [1, 1.3, 1],
                    opacity: [0.5, 1, 0.5]
                }}
                transition={{ duration: 1.5, delay: i * 0.15, repeat: Infinity }}
            />
        ))}

        {/* Connections */}
        {[
            { x1: 200, y1: 120, x2: 140, y2: 160 },
            { x1: 200, y1: 120, x2: 260, y2: 160 },
            { x1: 140, y1: 160, x2: 120, y2: 220 },
            { x1: 140, y1: 160, x2: 200, y2: 200 },
            { x1: 260, y1: 160, x2: 200, y2: 200 },
            { x1: 260, y1: 160, x2: 280, y2: 220 },
            { x1: 120, y1: 220, x2: 150, y2: 280 },
            { x1: 200, y1: 200, x2: 150, y2: 280 },
            { x1: 200, y1: 200, x2: 250, y2: 280 },
            { x1: 280, y1: 220, x2: 250, y2: 280 },
            { x1: 150, y1: 280, x2: 200, y2: 320 },
            { x1: 250, y1: 280, x2: 200, y2: 320 },
        ].map((line, i) => (
            <motion.line
                key={`conn-${i}`}
                x1={line.x1}
                y1={line.y1}
                x2={line.x2}
                y2={line.y2}
                stroke="#8b5cf6"
                strokeWidth="2"
                opacity="0.4"
                animate={{ opacity: [0.2, 0.8, 0.2] }}
                transition={{ duration: 1, delay: i * 0.1, repeat: Infinity }}
            />
        ))}

        {/* Data pulses */}
        {[0, 1, 2].map((_, i) => (
            <motion.circle
                key={`pulse-${i}`}
                cx={200}
                cy={200}
                r={50}
                fill="none"
                stroke="#22d3ee"
                strokeWidth="2"
                animate={{
                    r: [50, 150],
                    opacity: [0.8, 0]
                }}
                transition={{ duration: 2, delay: i * 0.7, repeat: Infinity }}
            />
        ))}

        {/* Binary data */}
        {[...Array(10)].map((_, i) => (
            <motion.text
                key={`binary-${i}`}
                x={50 + Math.random() * 300}
                y={50 + Math.random() * 300}
                fill="#22d3ee"
                fontSize="14"
                fontFamily="monospace"
                opacity="0.4"
                animate={{ opacity: [0.2, 0.6, 0.2] }}
                transition={{ duration: 2, delay: i * 0.2, repeat: Infinity }}
            >
                {Math.random() > 0.5 ? '1' : '0'}
            </motion.text>
        ))}
    </svg>
);

// Animated UI/UX Design Illustration
const UIUXDesignIllustration = () => (
    <svg viewBox="0 0 400 400" className="w-full h-full">
        <defs>
            <linearGradient id="uiGradient" x1="0%" y1="0%" x2="100%" y2="100%">
                <stop offset="0%" stopColor="#f472b6" />
                <stop offset="100%" stopColor="#8b5cf6" />
            </linearGradient>
        </defs>

        {/* Design canvas */}
        <motion.rect
            x="80" y="60" width="240" height="280" rx="15"
            fill="#1e293b"
            stroke="url(#uiGradient)"
            strokeWidth="3"
            animate={{ boxShadow: ['0 0 20px #f472b6', '0 0 40px #8b5cf6', '0 0 20px #f472b6'] }}
            transition={{ duration: 3, repeat: Infinity }}
        />

        {/* UI Elements */}
        {/* Header */}
        <motion.rect x="100" y="80" width="200" height="40" rx="5" fill="#8b5cf6" opacity="0.8"
            initial={{ scaleX: 0 }}
            animate={{ scaleX: 1 }}
            transition={{ duration: 0.5, repeat: Infinity, repeatDelay: 4 }}
        />

        {/* Sidebar */}
        <motion.rect x="100" y="130" width="60" height="190" rx="5" fill="#f472b6" opacity="0.6"
            initial={{ scaleY: 0 }}
            animate={{ scaleY: 1 }}
            transition={{ duration: 0.5, delay: 0.2, repeat: Infinity, repeatDelay: 4 }}
        />

        {/* Content cards */}
        {[0, 1, 2].map((row) => (
            [0, 1].map((col) => (
                <motion.rect
                    key={`card-${row}-${col}`}
                    x={175 + col * 65}
                    y={135 + row * 60}
                    width="55"
                    height="50"
                    rx="5"
                    fill="#22d3ee"
                    opacity="0.5"
                    initial={{ scale: 0 }}
                    animate={{ scale: 1 }}
                    transition={{ duration: 0.3, delay: 0.4 + (row * 2 + col) * 0.1, repeat: Infinity, repeatDelay: 4 }}
                />
            ))
        ))}

        {/* Cursor */}
        <motion.g
            animate={{
                x: [0, 100, 150, 50, 0],
                y: [0, 50, 100, 150, 0]
            }}
            transition={{ duration: 5, repeat: Infinity, ease: "easeInOut" }}
        >
            <motion.path
                d="M200 200 L200 230 L210 220 L220 235 L225 232 L215 217 L225 215 Z"
                fill="#ffffff"
                stroke="#000000"
                strokeWidth="1"
            />
        </motion.g>

        {/* Design tools floating */}
        {/* Pen */}
        <motion.g
            animate={{ y: [0, -10, 0], rotate: [0, 10, 0] }}
            transition={{ duration: 3, repeat: Infinity }}
        >
            <rect x="40" y="150" width="30" height="80" rx="3" fill="#f472b6" />
            <polygon points="55,230 40,260 70,260" fill="#1e293b" />
        </motion.g>

        {/* Color palette */}
        <motion.g
            animate={{ y: [0, -8, 0] }}
            transition={{ duration: 2.5, delay: 0.5, repeat: Infinity }}
        >
            <circle cx="355" cy="150" r="20" fill="#ef4444" />
            <circle cx="355" cy="195" r="20" fill="#22c55e" />
            <circle cx="355" cy="240" r="20" fill="#3b82f6" />
            <circle cx="355" cy="285" r="20" fill="#fbbf24" />
        </motion.g>
    </svg>
);

// Animated Offshore Development Illustration
const OffshoreDevelopmentIllustration = () => (
    <svg viewBox="0 0 400 400" className="w-full h-full">
        <defs>
            <linearGradient id="globeGradient" x1="0%" y1="0%" x2="100%" y2="100%">
                <stop offset="0%" stopColor="#0d9488" />
                <stop offset="100%" stopColor="#0369a1" />
            </linearGradient>
        </defs>

        {/* Globe */}
        <motion.circle
            cx="200" cy="200" r="120"
            fill="url(#globeGradient)"
            stroke="#22d3ee"
            strokeWidth="3"
            animate={{ rotate: 360 }}
            transition={{ duration: 30, repeat: Infinity, ease: "linear" }}
            style={{ originX: '200px', originY: '200px' }}
        />

        {/* Latitude lines */}
        {[-60, -30, 0, 30, 60].map((lat, i) => (
            <motion.ellipse
                key={`lat-${i}`}
                cx="200"
                cy={200 + lat}
                rx={120 * Math.cos((lat * Math.PI) / 180)}
                ry="10"
                fill="none"
                stroke="#22d3ee"
                strokeWidth="1"
                opacity="0.3"
            />
        ))}

        {/* Longitude lines */}
        {[0, 45, 90, 135].map((lon, i) => (
            <motion.ellipse
                key={`lon-${i}`}
                cx="200"
                cy="200"
                rx="10"
                ry="120"
                fill="none"
                stroke="#22d3ee"
                strokeWidth="1"
                opacity="0.3"
                style={{ transform: `rotate(${lon}deg)`, transformOrigin: '200px 200px' }}
            />
        ))}

        {/* Team members around globe */}
        {[
            { x: 80, y: 150, delay: 0 },
            { x: 320, y: 150, delay: 0.3 },
            { x: 80, y: 280, delay: 0.6 },
            { x: 320, y: 280, delay: 0.9 },
        ].map((member, i) => (
            <motion.g key={`member-${i}`}>
                {/* Avatar */}
                <motion.circle
                    cx={member.x}
                    cy={member.y}
                    r="25"
                    fill="#1e293b"
                    stroke="#22d3ee"
                    strokeWidth="2"
                    animate={{ scale: [1, 1.1, 1] }}
                    transition={{ duration: 2, delay: member.delay, repeat: Infinity }}
                />
                {/* Head */}
                <motion.circle
                    cx={member.x}
                    cy={member.y - 5}
                    r="10"
                    fill="#22d3ee"
                    animate={{ scale: [1, 1.05, 1] }}
                    transition={{ duration: 2, delay: member.delay, repeat: Infinity }}
                />
                {/* Body */}
                <motion.path
                    d={`M ${member.x - 12} ${member.y + 15} Q ${member.x} ${member.y + 5} ${member.x + 12} ${member.y + 15}`}
                    fill="#22d3ee"
                    animate={{ scale: [1, 1.05, 1] }}
                    transition={{ duration: 2, delay: member.delay, repeat: Infinity }}
                />
                {/* Connection line to globe */}
                <motion.line
                    x1={member.x}
                    y1={member.y}
                    x2={200}
                    y2={200}
                    stroke="#22d3ee"
                    strokeWidth="2"
                    strokeDasharray="5 5"
                    opacity="0.5"
                    animate={{ opacity: [0.3, 0.8, 0.3] }}
                    transition={{ duration: 1.5, delay: member.delay, repeat: Infinity }}
                />
            </motion.g>
        ))}

        {/* Data packets traveling */}
        {[0, 1, 2, 3].map((_, i) => (
            <motion.circle
                key={`packet-${i}`}
                r="6"
                fill="#fbbf24"
                animate={{
                    cx: [80 + (i % 2) * 240, 200, 80 + (i % 2) * 240],
                    cy: [150 + Math.floor(i / 2) * 130, 200, 150 + Math.floor(i / 2) * 130]
                }}
                transition={{ duration: 2, delay: i * 0.5, repeat: Infinity, ease: "easeInOut" }}
            />
        ))}

        {/* "24/7" badge */}
        <motion.g
            animate={{ scale: [1, 1.1, 1] }}
            transition={{ duration: 2, repeat: Infinity }}
        >
            <circle cx="200" cy="200" r="35" fill="#0f172a" stroke="#fbbf24" strokeWidth="3" />
            <text x="200" y="205" textAnchor="middle" fill="#fbbf24" fontSize="16" fontWeight="bold">24/7</text>
        </motion.g>
    </svg>
);

const solutions = [
    {
        id: 1,
        title: 'Product Development',
        description: 'End-to-end development of websites, mobile apps, and cloud systems built for scale.',
        Illustration: ProductDevelopmentIllustration,
        link: '/services'
    },
    {
        id: 2,
        title: 'MVP Development',
        description: 'Launch your startup idea faster with lean, focused development approach.',
        Illustration: MVPDevelopmentIllustration,
        link: '/services'
    },
    {
        id: 3,
        title: 'AI/ML & Data Engineering',
        description: 'Turn data into insights with machine learning and intelligent automation.',
        Illustration: AIMLIllustration,
        link: '/services'
    },
    {
        id: 4,
        title: 'UI/UX Design',
        description: 'Intuitive, engaging designs that turn users into loyal customers.',
        Illustration: UIUXDesignIllustration,
        link: '/services'
    },
    {
        id: 5,
        title: 'Offshore Development',
        description: 'High-performance remote development teams that integrate seamlessly.',
        Illustration: OffshoreDevelopmentIllustration,
        link: '/services'
    }
];

const CuttingEdgeSolutions = () => {
    const [activeIndex, setActiveIndex] = useState(0);

    const ActiveIllustration = solutions[activeIndex].Illustration;

    return (
        <section className="py-32 relative overflow-hidden bg-[#0a0a0a]">
            {/* Animated background */}
            <div className="absolute inset-0">
                <div className="absolute top-0 left-0 w-full h-full opacity-10 bg-[radial-gradient(ellipse_at_top,_var(--tw-gradient-stops))] from-cyan-900 via-[#0a0a0a] to-[#0a0a0a]" />
                <motion.div
                    className="absolute top-20 right-20 w-96 h-96 bg-teal-500/10 rounded-full blur-3xl"
                    animate={{ scale: [1, 1.2, 1], opacity: [0.1, 0.2, 0.1] }}
                    transition={{ duration: 8, repeat: Infinity }}
                />
                <motion.div
                    className="absolute bottom-20 left-20 w-80 h-80 bg-cyan-500/10 rounded-full blur-3xl"
                    animate={{ scale: [1.2, 1, 1.2], opacity: [0.15, 0.1, 0.15] }}
                    transition={{ duration: 6, repeat: Infinity }}
                />
            </div>

            <div className="container mx-auto px-6 relative z-10">
                {/* Section Title */}
                <motion.div
                    className="text-center mb-16"
                    initial={{ opacity: 0, y: 30 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true }}
                    transition={{ duration: 0.6 }}
                >
                    <h2 className="text-3xl md:text-4xl font-bold text-white tracking-widest mb-4 font-mono">
                        CUTTING EDGE SOLUTIONS
                    </h2>
                    <motion.div
                        className="w-16 h-1 bg-gradient-to-r from-teal-500 to-cyan-400 mx-auto"
                        initial={{ width: 0 }}
                        whileInView={{ width: 64 }}
                        viewport={{ once: true }}
                        transition={{ duration: 0.8, delay: 0.3 }}
                    />
                </motion.div>

                {/* Main Content Grid */}
                <div className="grid lg:grid-cols-2 gap-8 items-center">
                    {/* Left: Service Cards - responsive grid for 5 items */}
                    <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                        {solutions.map((solution, index) => (
                            <motion.div
                                key={solution.id}
                                onMouseEnter={() => setActiveIndex(index)}
                                onClick={() => setActiveIndex(index)}
                                className={`
                  relative p-4 min-h-[140px] rounded-xl cursor-pointer transition-all duration-500
                  ${activeIndex === index
                                        ? 'bg-gradient-to-br from-teal-500 to-teal-700 text-white shadow-2xl shadow-teal-500/30 scale-105 z-10 col-span-1'
                                        : 'bg-white/5 backdrop-blur-sm text-slate-300 hover:bg-white/10 border border-white/10'
                                    }
                  ${index === 4 ? 'md:col-start-2' : ''}
                `}
                                initial={{ opacity: 0, y: 20 }}
                                whileInView={{ opacity: 1, y: 0 }}
                                viewport={{ once: true }}
                                transition={{ duration: 0.4, delay: index * 0.1 }}
                                whileHover={{ y: -5 }}
                            >
                                {/* Glow effect for active card */}
                                {activeIndex === index && (
                                    <motion.div
                                        className="absolute inset-0 rounded-xl bg-teal-400/20 blur-xl"
                                        initial={{ opacity: 0 }}
                                        animate={{ opacity: 1 }}
                                        exit={{ opacity: 0 }}
                                    />
                                )}

                                <div className="relative z-10">
                                    <h3 className={`
                    text-sm md:text-base font-bold mb-2 font-sans transition-colors duration-300 leading-tight
                    ${activeIndex === index ? 'text-white' : 'text-teal-400'}
                  `}>
                                        {solution.title}
                                    </h3>

                                    <AnimatePresence>
                                        {activeIndex === index && (
                                            <motion.div
                                                initial={{ opacity: 0, y: 10 }}
                                                animate={{ opacity: 1, y: 0 }}
                                                exit={{ opacity: 0, y: 10 }}
                                                transition={{ duration: 0.3 }}
                                                className="mt-auto pt-4"
                                            >
                                                <Link
                                                    to={solution.link}
                                                    className="inline-flex items-center gap-2 px-4 py-2 bg-white/20 backdrop-blur-sm border border-white/30 text-white text-xs font-semibold rounded-lg hover:bg-white/30 transition-all duration-300 group"
                                                >
                                                    READ MORE
                                                    <motion.span
                                                        animate={{ x: [0, 5, 0] }}
                                                        transition={{ duration: 1, repeat: Infinity }}
                                                    >
                                                        →
                                                    </motion.span>
                                                </Link>
                                            </motion.div>
                                        )}
                                    </AnimatePresence>
                                </div>
                            </motion.div>
                        ))}
                    </div>

                    {/* Right: Dynamic Animated Illustration */}
                    <motion.div
                        className="relative h-[400px] lg:h-[450px] rounded-2xl overflow-hidden bg-gradient-to-br from-slate-900/50 to-slate-800/30 backdrop-blur-sm border border-white/10"
                        initial={{ opacity: 0, x: 50 }}
                        whileInView={{ opacity: 1, x: 0 }}
                        viewport={{ once: true }}
                        transition={{ duration: 0.6 }}
                    >
                        <AnimatePresence mode="wait">
                            <motion.div
                                key={activeIndex}
                                initial={{ opacity: 0, scale: 0.9, rotate: -5 }}
                                animate={{ opacity: 1, scale: 1, rotate: 0 }}
                                exit={{ opacity: 0, scale: 1.1, rotate: 5 }}
                                transition={{ duration: 0.5, ease: "easeOut" }}
                                className="absolute inset-0 p-6"
                            >
                                <ActiveIllustration />
                            </motion.div>
                        </AnimatePresence>

                        {/* Floating label */}
                        <motion.div
                            className="absolute bottom-6 left-6 right-6 p-4 bg-black/60 backdrop-blur-md rounded-xl border border-white/20"
                            initial={{ opacity: 0, y: 20 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{ delay: 0.3 }}
                        >
                            <motion.h3
                                key={`title-${activeIndex}`}
                                className="text-xl font-bold text-white mb-1"
                                initial={{ opacity: 0, x: -20 }}
                                animate={{ opacity: 1, x: 0 }}
                                transition={{ duration: 0.3 }}
                            >
                                {solutions[activeIndex].title}
                            </motion.h3>
                            <motion.p
                                key={`desc-${activeIndex}`}
                                className="text-slate-300 text-sm"
                                initial={{ opacity: 0 }}
                                animate={{ opacity: 1 }}
                                transition={{ duration: 0.3, delay: 0.1 }}
                            >
                                {solutions[activeIndex].description}
                            </motion.p>
                        </motion.div>
                    </motion.div>
                </div>

                {/* Pagination Dots */}
                <div className="flex justify-center gap-3 mt-12">
                    {solutions.map((_, index) => (
                        <motion.button
                            key={index}
                            onClick={() => setActiveIndex(index)}
                            onMouseEnter={() => setActiveIndex(index)}
                            className={`
                h-3 rounded-full transition-all duration-500
                ${activeIndex === index
                                    ? 'bg-gradient-to-r from-teal-400 to-cyan-400 w-8'
                                    : 'bg-slate-600 hover:bg-slate-500 w-3'
                                }
              `}
                            whileHover={{ scale: 1.2 }}
                            whileTap={{ scale: 0.9 }}
                            aria-label={`Go to slide ${index + 1}`}
                        />
                    ))}
                </div>
            </div>
        </section>
    );
};

export default CuttingEdgeSolutions;
